import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import { logAudit } from '@/lib/logAudit';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const resolveRedirect = async (userId: string): Promise<string> => {
    const { data: sa } = await supabase
      .from('saas_super_admins')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    return sa ? '/sa/dashboard' : '/admin';
  };

  useEffect(() => {
    const check = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        const dest = await resolveRedirect(data.session.user.id);
        navigate(dest, { replace: true });
      }
    };
    check();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      Swal.fire('Campos requeridos', 'Por favor ingresa tu email y contraseña.', 'warning');
      return;
    }
    setLoading(true);
    const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      Swal.fire('Error de acceso', error.message, 'error');
    } else {
      const dest = await resolveRedirect(authData.user!.id);
      logAudit({ action: 'login', entity: 'user', entityName: email }).catch(() => {});
      navigate(dest);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neutral-950 relative overflow-hidden"
      style={{ fontFamily: '\'Inter\', sans-serif' }}
    >
      {/* Background ambient */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[-120px] left-[-120px] w-[500px] h-[500px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-100px] right-[-100px] w-[420px] h-[420px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #d97706 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo + Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-lg">
              <i className="ri-camera-3-line text-neutral-950 text-lg" />
            </div>
            <span className="text-white text-2xl font-bold tracking-tight">
              Pixory <span className="text-amber-400">AI</span>
            </span>
          </div>
          <p className="text-neutral-500 text-sm">Panel de administración</p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleLogin}
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 flex flex-col gap-6"
        >
          <div>
            <h1 className="text-white text-xl font-semibold mb-1">Iniciar sesión</h1>
            <p className="text-neutral-500 text-sm">Accede a tu espacio de trabajo</p>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-400 text-xs font-medium uppercase tracking-wider">
              Correo electrónico
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-mail-line text-neutral-500 text-sm" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                autoComplete="email"
                className="w-full bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-600 rounded-lg pl-9 pr-4 py-3 focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-400 text-xs font-medium uppercase tracking-wider">
              Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-lock-line text-neutral-500 text-sm" />
              </div>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-600 rounded-lg pl-9 pr-10 py-3 focus:outline-none focus:border-amber-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-neutral-950 font-semibold text-sm rounded-lg py-3 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <i className="ri-login-box-line text-base" />
                Iniciar sesión
              </>
            )}
          </button>

          {/* Link to register */}
          <p className="text-center text-neutral-500 text-sm">
            ¿No tenés cuenta?{' '}
            <Link to="/register" className="text-amber-400 hover:text-amber-300 transition-colors font-medium">
              Crear una cuenta
            </Link>
          </p>
        </form>

        <p className="text-center text-neutral-700 text-xs mt-6">
          © 2026 Pixory AI · Todos los derechos reservados
        </p>
      </div>
    </div>
  );
}